import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ParticlesBackground from './ParticlesBackground';
import api from "../axiosConfig";
import { useGuestPreview } from "../context/GuestPreviewContext";
import { setLoggedInUserEmail, clearLegacySearchSessionStorage } from "../utils/searchSessionStorage";

const PENDING_INIT_INTERESTS_KEY = "gradconnect_pending_init_interests";

const Login = () => {
    const [formData, setFormData] = useState({
        email: '',
        password: '',
    });
    const [error, setError] = useState(''); // Added error state
    const navigate = useNavigate();
    const location = useLocation();
    const { clearGuestPreview } = useGuestPreview();

    // Already signed in — skip the login form (Recommendations redirects to init if needed).
    // Guest preview users may still have a stale token in localStorage; they must see the form
    // when coming from SignUpToContinueModal ("Log in").
    useEffect(() => {
        if (location.state?.fromGuestPreview === true) {
            return;
        }
        if (localStorage.getItem('authToken')) {
            navigate('/recommendations', { replace: true });
        }
    }, [navigate, location.state]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
        const response = await api.post('/login/', formData);

        if (response.status >= 200 && response.status < 300) { // Axios doesn't use `ok`, so check the status range
            const data = response.data;
            console.log('Login successful:', data);

            // Save both tokens to localStorage
            localStorage.setItem('authToken', data.access); // Save access token to localStorage
            localStorage.setItem('refreshToken', data.refresh); // Save refresh token to localStorage

            setLoggedInUserEmail(data.email);
            clearLegacySearchSessionStorage();

            clearGuestPreview();
            const researchInterests = location.state?.researchInterests || '';
            const fromGuest = location.state?.fromGuestPreview === true;

            if (fromGuest || researchInterests) {
                let alreadyInitialized = false;
                try {
                    const initRes = await api.get("/get-whether-initialization-complete/");
                    alreadyInitialized = !!initRes.data?.initialization_complete;
                } catch (e) {
                    // Prefer recommendations; that page re-checks init and sends new users to initialize if needed.
                    console.error("Could not check initialization after login:", e);
                    alreadyInitialized = true;
                }

                if (alreadyInitialized) {
                    sessionStorage.removeItem(PENDING_INIT_INTERESTS_KEY);
                    navigate("/recommendations", { state: { email: data.email } });
                } else {
                    if (researchInterests) {
                        sessionStorage.setItem(PENDING_INIT_INTERESTS_KEY, researchInterests);
                    }
                    navigate("/initialize-recommendations", {
                        state: { email: data.email, researchInterests },
                    });
                }
            } else {
                navigate('/recommendations', { state: { email: data.email } });
            }
        }
    } catch (error) {
        const errorData = error.response?.data || {};
        console.error('Error logging in:', errorData.message);
        setError('Login failed: ' + (errorData.message || 'An unknown error occurred')); // Set error message
    }
};

    return (
        <div style={{ position: 'relative', minHeight: '100vh' }}>
            <ParticlesBackground /> {/* Add the Particles background */}
            <div className="flex items-center justify-center min-h-screen py-4 px-4">
                <form
                    onSubmit={handleSubmit}
                    className="bg-white p-10 rounded-lg shadow-lg w-full sm:w-96 md:w-3/5 lg:w-2/5 xl:w-1/3 max-w-lg mx-auto"  // Adjusted form width for larger breakpoints
                    style={{
                        backgroundColor: 'white',
                        zIndex: 1,
                        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                    }}
                >
                    <h2 className="text-center text-3xl font-semibold text-[#202531] mb-6">
                        Sign In
                    </h2>

                    {/* Error Message */}
                    {error && (
                        <div className="mb-4 text-center text-red-500">
                            <p>{error}</p>
                        </div>
                    )}

                    <div className="mb-6">
                        <label htmlFor="email" className="block text-[#202531] mb-2 text-left">
                            Email
                        </label>
                        <input
                            type="email"
                            name="email"
                            id="email"
                            placeholder="Enter email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="w-full p-4 border border-gray-300 rounded-md bg-[#F2F2F2]"
                            style={{
                                backgroundColor: '#F2F2F2',
                                borderColor: '#D1D5DB',
                                color: '#202531',
                            }}
                        />
                    </div>

                    <div className="mb-6">
                        <label htmlFor="password" className="block text-[#202531] mb-2 text-left">
                            Password
                        </label>
                        <input
                            type="password"
                            name="password"
                            id="password"
                            placeholder="Enter password"
                            value={formData.password}
                            onChange={handleChange}
                            required
                            className="w-full p-4 border border-gray-300 rounded-md bg-[#F2F2F2]"
                            style={{
                                backgroundColor: '#F2F2F2',
                                borderColor: '#D1D5DB',
                                color: '#202531',
                            }}
                        />
                    </div>

                    <div className="mb-2">
                        <a
                            href="/forgot-password"
                            className="text-[#0196BF] text-sm hover:underline"
                        >
                            Forgot Password?
                        </a>
                    </div>

                    <button
                        type="submit"
                        className="mt-2 bg-[#0196BF] text-white font-bold text-xl px-24 py-3 rounded-full hover:bg-[#017BA3] transition z-20 max-w-full mx-auto flex items-center justify-center"
                    >
                        Login
                    </button>

                    <div className="mt-4 text-center">
                        <p className="text-sm">
                            Need an account?{' '}
                            <a
                                href="/register"
                                className="text-[#0196BF] hover:underline"
                            >
                                Sign up
                            </a>
                        </p>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Login;
